import { Affix, Alert, Box, Button, Group, Paper, Text, Transition } from "@mantine/core";
import {
  CLINICIAN_LABEL_FIELDS,
  ClinicianLabelDraftMap,
  buildClinicianReviewPayload,
  draftsFromLabels,
  isClinicianLabelDraftComplete,
  rememberClinicianReviewAnswered,
  wasClinicianReviewAnswered,
} from "@utils/clinicianLabelUtils";
import { ClinicianLabelField, ClinicianLabels, ClinicianReviewPayload } from "types/tests";
import { useEffect, useMemo, useState } from "react";

import ClinicianLabelFields from "./ClinicianLabelFields";
import {
  CLINICIAN_REVIEW_BANNER_Z_INDEX,
  CLINICIAN_REVIEW_PROMPT_DELAY_MS,
} from "@constants/clinicianLabelConstants";
import { useTranslation } from "react-i18next";

interface ClinicianReviewBannerProps {
  testUuid: string;
  labels?: ClinicianLabels;
  saving?: boolean;
  onSave: (payload: ClinicianReviewPayload) => Promise<void>;
}

export default function ClinicianReviewBanner({
  testUuid,
  labels,
  saving,
  onSave,
}: ClinicianReviewBannerProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [drafts, setDrafts] = useState<ClinicianLabelDraftMap>(() => draftsFromLabels(labels));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setVisible(false);
    setFailed(false);
    if (wasClinicianReviewAnswered(testUuid)) return;
    const timer = setTimeout(() => setVisible(true), CLINICIAN_REVIEW_PROMPT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [testUuid]);

  useEffect(() => {
    if (!visible) setDrafts(draftsFromLabels(labels));
  }, [labels, visible]);

  const payload = useMemo(() => buildClinicianReviewPayload(drafts, labels), [drafts, labels]);
  const incomplete = CLINICIAN_LABEL_FIELDS.some(
    (field) => !isClinicianLabelDraftComplete(drafts[field]),
  );

  function handleChange(field: ClinicianLabelField, draft: ClinicianLabelDraftMap[typeof field]) {
    setDrafts((current) => ({ ...current, [field]: draft }));
  }

  function dismiss() {
    rememberClinicianReviewAnswered(testUuid);
    setVisible(false);
  }

  async function handleSave() {
    if (!payload) return;
    setFailed(false);
    try {
      await onSave(payload);
      setVisible(false);
    } catch {
      setFailed(true);
    }
  }

  return (
    <Affix
      className="no-print"
      position={{ bottom: 0, left: 0, right: 0 }}
      zIndex={CLINICIAN_REVIEW_BANNER_Z_INDEX}
      data-testid="clinician-review-banner"
    >
      <Transition transition="slide-up" duration={250} mounted={visible}>
        {(transitionStyles) => (
          <Paper
            style={transitionStyles}
            withBorder
            shadow="lg"
            radius={0}
            px="md"
            py="sm"
            w="100%"
          >
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
              <Box flex="1 1 240px" miw={0} mah="45vh" style={{ overflowY: "auto" }}>
                <Text fw={600}>{t("ClinicianLabel.bannerTitle")}</Text>
                <Text size="sm" c="dimmed" mb="xs">
                  {t("ClinicianLabel.bannerSubtitle")}
                </Text>
                {failed && (
                  <Alert color="red" mb="xs" py={4} data-testid="clinician-review-error">
                    {t("ClinicianLabel.saveFailed")}
                  </Alert>
                )}
                <Group align="flex-end" gap="sm">
                  {CLINICIAN_LABEL_FIELDS.map((field) => (
                    <ClinicianLabelFields
                      key={field}
                      field={field}
                      draft={drafts[field]}
                      onChange={handleChange}
                      disabled={saving}
                      flex="1 1 180px"
                    />
                  ))}
                </Group>
              </Box>
              <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                <Button variant="default" onClick={dismiss} data-testid="clinician-review-dismiss">
                  {t("ClinicianLabel.notNow")}
                </Button>
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={!payload || incomplete}
                  data-testid="clinician-review-save"
                >
                  {t("ClinicianLabel.save")}
                </Button>
              </Group>
            </Group>
          </Paper>
        )}
      </Transition>
    </Affix>
  );
}
